import type { LiveCustomJoinPage } from "./customJoinPage";
import { Niche } from "./niche";
import { Program } from "./program";

export interface SocialMediaPlatform {
  id: string;
  name: string;
  url: string;
  iconUrl: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SocialMedia {
  id: string;
  username: string;
  socialMediaPlatformId: string;
  brandDetailId: string;
  createdAt: string;
  updatedAt: string;
  socialMediaPlatform: SocialMediaPlatform;
}

export interface BrandUser {
  id: string;
  name: string;
  profileImage: string | null;
  email: string;
  shop: string;
  role: "BRAND";
  companyName: string | null;
}
/** A react-easy-crop area in percentages (0-100) of the original image. */
export interface BannerCropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Framing of the uploaded banner for the templated join page's strips. */
export interface BannerCrop {
  desktop?: BannerCropArea;
  mobile?: BannerCropArea;
}

export interface BrandDetails {
  [x: string]: unknown;
  niches: Niche[];
  id: string;
  username: string;
  profileImage: string | null;
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  timezone: string;
  companyName: string | null;
  website: string | null;
  description: string | null;
  metaTitle?: string | null;
  metaDescription?: string | null;
  metaImage?: string | null;
  sessionId: string;
  brandBannerImage: string | null;
  brandBannerImageMobile: string | null;
  /** Banner as uploaded, before the 5:1 crop (custom join page hero). */
  brandBannerImageOriginal?: string | null;
  /** How the original is framed into the 5:1 / 2:1 strips; null = centre crop. */
  brandBannerCrop?: BannerCrop | null;
  brandProfileImage: string | null;
  createdAt: string;
  updatedAt: string;
  onboardingStatus: string;
  isEmailAutomationEnabled: boolean;
  isDevStore?: boolean;
  brandAppearance: AppearanceSettings;
  // Brand-authored page, present only when one is live. See types/customJoinPage.
  customJoinPage?: LiveCustomJoinPage | null;
  program: Program;
  social?: BrandSocial;
  socialMedia?: SocialMedia[];
  stats: BrandStats;
  attributionMethod?: 'STATIC_CODE' | 'SNEAKYLINK';
  shortDiscountUrl?: boolean;
  needsShortUrlReauth?: boolean;
  // A `?p=` token was supplied but no longer resolves to a joinable program of
  // this brand — `program` is the default one, shown as a fallback.
  programTokenInvalid?: boolean;
  // `program` came from a share link rather than being the brand's default.
  isSharedProgram?: boolean;
}

export interface BrandSocial {
  facebook?: string;
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  discord?: string;
}

export interface BrandStats {
  rank: number | string;
  products: number | string;
  team: number | string;
  orders: number | string;
  joined: string;
  totalAffiliates?: number;
  totalAffiliateSales?: number;
  totalAffiliatePayouts?: number;
}


export interface BrandMinimal {
  id: string;
  name:string;
  programId:string;
}

export interface AppearanceSettings {
  joinText: string;
  joinTitle?: string;
  ambassadorText: string;
  theme: string;
  primayColour: string;
  secondaryColour: string;
  socialMediaIconSizes: string;
  imageIds: string[];
  showcaseProductIds: string[];
}
export const ICON_SIZES = [
  { value: "w-4 h-4", label: "Small", size: "w-4 h-4" },
  { value: "w-5 h-5", label: "Medium", size: "w-5 h-5" },
  { value: "w-6 h-6", label: "Large", size: "w-6 h-6" },
];

export type ThankYouBannerLayout = "STYLE_1" | "STYLE_2" | "STYLE_3";

export interface ThankYouBannerConfig {
  id: string | null;
  bannerLogo: string | null;
  headlineTemplate: string;
  includeCustomerName: boolean;
  commissionMessage: string;
  layoutStyle: ThankYouBannerLayout;
  socialMediaIds: string[];
  isEnabled: boolean;
  isConfigured: boolean;
  commissionType: string;
  commissionValue: string;
  discountType: string;
  discountValue: string;
}

export interface ThankYouBannerSocialOption {
  id: string;
  username: string;
  platformId: string;
  platformName: string;
  iconUrl: string | null;
}

export interface ThankYouBannerManagementResponse {
  config: ThankYouBannerConfig;
  availableSocialMedia: ThankYouBannerSocialOption[];
}

export interface UpdateThankYouBannerPayload {
  bannerLogo?: string | null;
  headlineTemplate?: string;
  includeCustomerName?: boolean;
  commissionMessage?: string;
  layoutStyle?: ThankYouBannerLayout;
  socialMediaIds?: string[];
  isEnabled?: boolean;
  commissionType?: string;
  commissionValue?: string;
  discountType?: string;
  discountValue?: string;
}

export const THANK_YOU_BANNER_LAYOUTS: Record<
  ThankYouBannerLayout,
  { label: string; description: string; accent: string }
> = {
  STYLE_1: {
    label: "Spotlight",
    description: "Large hero image with stacked copy and CTA button.",
    accent: "border-brand-200 bg-brand-50",
  },
  STYLE_2: {
    label: "Split",
    description: "Side-by-side layout perfect for short copy + socials.",
    accent: "border-blue-200 bg-blue-50",
  },
  STYLE_3: {
    label: "Minimal",
    description: "Compact banner that focuses on text and CTA.",
    accent: "border-emerald-200 bg-emerald-50",
  },
};

export const THEME_CONFIGS = {
  TYPE_1: {
    name: "Modern Minimal",
    description: "Clean, simple design with subtle shadows",
    spacing: "space-y-4",
    fontFamily: "font-sans",
    fontSize: "text-base",
    lineHeight: "leading-tight",
    borderRadius: "rounded-lg",
    borderWidth: "border",
    shadow: "shadow-sm",
    animation: "transition-all duration-200",
  },
  TYPE_2: {
    name: "Bold & Vibrant",
    description: "High contrast with bold styling",
    spacing: "space-y-2",
    fontFamily: "font-sans",
    fontSize: "text-lg",
    lineHeight: "leading-tight",
    borderRadius: "rounded-xl",
    borderWidth: "border-2",
    shadow: "shadow-lg",
    animation: "animate-bounce-fade",
  },
  TYPE_3: {
    name: "Elegant Professional",
    description: "Sophisticated design with refined styling",
    spacing: "space-y-4",
    fontFamily: "font-sans",
    fontSize: "text-sm",
    lineHeight: "leading-tight",
    borderRadius: "rounded-md",
    borderWidth: "border",
    shadow: "shadow-md",
    animation: "transition-opacity duration-300",
  },
  TYPE_4: {
    name: "Creative Playful",
    description: "Fun and energetic with dynamic styling",
    spacing: "space-y-4",
    fontFamily: "font-mono",
    fontSize: "text-lg",
    lineHeight: "leading-relaxed",
    borderRadius: "rounded-2xl",
    borderWidth: "border-4",
    shadow: "shadow-xl",
    animation: "transition-transform duration-300",
  },
};



export interface BrandMinimal {
  id: string;
  name:string;
  programId:string;
}